import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import Table from 'apollo-react/components/Table';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  addTableRow,
  deleteTableRow,
  editTableRow,
  moveTableRow,
  setSelectedRow,
  validateSavedSearch,
} from './../actions/editor.actions';
import {
  getEntityTreeFieldsWithFilter,
  getSavedSearchTableRows,
  getSelectedTableRow,
} from './../selectors/editor.selectors';
import ExpandablePanel from './ExpandablePanel';
import ActionCell from './table/ActionCell';
import CheckBoxCell from './table/CheckBoxCell';
import ColumnCell from './table/ColumnCell';
import EditableCell from './table/EditableCell';
import EditableSelectCell from './table/EditableSelectCell';
import { useSavedSearchSelector, useTableLastRowVisibilityEffect } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  RELATIONAL_OPERATORS,
  SAVED_SEARCH_CRITERIA,
  SAVED_SEARCH_CRITERIA_TABLE_LABEL,
  SEARCH_CRITERIA_CLOSE_GROUP,
  SEARCH_CRITERIA_CLOSE_GROUP_OPTIONS,
  SEARCH_CRITERIA_COLUMN,
  SEARCH_CRITERIA_ENABLE_LOOKUP,
  SEARCH_CRITERIA_IS_EDIT_ALLOWED,
  SEARCH_CRITERIA_OPEN_GROUP,
  SEARCH_CRITERIA_OPEN_GROUP_OPTIONS,
  SEARCH_CRITERIA_OPERATOR,
  SEARCH_CRITERIA_RELATION_OPERATOR,
  SEARCH_CRITERIA_VALUE,
  SEARCH_CRITERIA_VALUE_OPTIONS,
  SQL_OPERATORS_OPTIONS,
} from '../constants/editor.constants';
import { getColumnsWithTranslations } from '../utils/common.utils';

const useStyles = makeStyles({
  tableContainer: {
    overflowX: 'auto',
    '& [class*="middleAlign"]': {
      height: '60px !important',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 600,
    marginTop: 5,
    marginRight: 35,
  },
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  selected: {
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
  select: {
    margin: 3,
    paddingRight: 3,
  },
});

const columns = [
  {
    header: '(',
    accessor: SEARCH_CRITERIA_OPEN_GROUP,
    customCell: EditableSelectCell('openGroupOptions'),
    width: 80,
  },
  {
    header: 'Column',
    accessor: SEARCH_CRITERIA_COLUMN,
    customCell: ColumnCell,
    width: 500,
  },
  {
    header: 'Operator',
    accessor: SEARCH_CRITERIA_RELATION_OPERATOR,
    customCell: EditableSelectCell('relationalOperatorOptions'),
    width: 140,
  },
  {
    header: 'Value',
    accessor: SEARCH_CRITERIA_VALUE,
    customCell: EditableCell,
    enableLookup: true,
    lookupValues: SEARCH_CRITERIA_VALUE_OPTIONS,
  },
  {
    header: ')',
    accessor: SEARCH_CRITERIA_CLOSE_GROUP,
    customCell: EditableSelectCell('closeGroupOptions'),
    width: 80,
  },
  {
    header: 'Relational Operator',
    accessor: SEARCH_CRITERIA_OPERATOR,
    customCell: EditableSelectCell('sqlOperatorOptions'),
    width: 100,
  },
  {
    header: 'Allow Editing',
    accessor: SEARCH_CRITERIA_IS_EDIT_ALLOWED,
    align: 'center',
    customCell: CheckBoxCell,
    width: 110,
  },
  {
    header: 'Enable Lookup',
    accessor: SEARCH_CRITERIA_ENABLE_LOOKUP,
    align: 'center',
    customCell: CheckBoxCell,
    width: 110,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
    width: 55,
  },
];

export const searchCriteriaTableValidationProps = [
  {
    prop: SEARCH_CRITERIA_COLUMN,
    errorLabel: 'Column',
    required: true,
    tableName: SAVED_SEARCH_CRITERIA_TABLE_LABEL,
  },
];

const SearchCriteriaTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const rows = useSavedSearchSelector(
    getSavedSearchTableRows,
    { tablePath: SAVED_SEARCH_CRITERIA },
    equalityFn
  );
  const entityColumns = useSelector(getEntityTreeFieldsWithFilter, equalityFn);
  const selectedTableRowId = useSelector(getSelectedTableRow);

  const tableRef = useRef();

  const tableName = SAVED_SEARCH_CRITERIA;

  useTableLastRowVisibilityEffect({ tableRef, rows });

  const columnsWithTranslations = useMemo(() => {
    return getColumnsWithTranslations({ columns, translateString });
  }, [translateString]);

  const addRow = () => {
    const newRow = {
      rowId: uuidv4(),
      [SEARCH_CRITERIA_OPEN_GROUP]: false,
      [SEARCH_CRITERIA_COLUMN]: '',
      [SEARCH_CRITERIA_RELATION_OPERATOR]: 'EQ',
      [SEARCH_CRITERIA_VALUE]: '',
      [SEARCH_CRITERIA_CLOSE_GROUP]: false,
      [SEARCH_CRITERIA_OPERATOR]: 'and',
      [SEARCH_CRITERIA_IS_EDIT_ALLOWED]: true,
      [SEARCH_CRITERIA_ENABLE_LOOKUP]: true,
    };

    dispatch(addTableRow({ tableName, newRow }));
    dispatch(setSelectedRow({ rowId: newRow.rowId }));
  };

  const editRow = (params) => {
    dispatch(editTableRow({ ...params, tableName }));
    params.error && dispatch(validateSavedSearch());
  };

  const deleteRow = (params) => {
    dispatch(deleteTableRow({ ...params, tableName }));
  };

  const moveRow = (params) => {
    dispatch(moveTableRow({ ...params, tableName }));
  };

  const handleSelectRow = ({ rowId }) => {
    dispatch(setSelectedRow({ rowId }));
  };

  return (
    <ExpandablePanel label={translateString(SAVED_SEARCH_CRITERIA_TABLE_LABEL)} addRow={addRow}>
      <Grid xs={12} className={classes.tableContainer} ref={tableRef}>
        <Table
          columns={columnsWithTranslations}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={rows.map((row, index) => ({
            ...row,
            classes,
            index,
            enableRowMovement: true,
            editRow,
            deleteRow,
            entityColumns,
            translateString,
            relationalOperatorOptions: RELATIONAL_OPERATORS,
            openGroupOptions: SEARCH_CRITERIA_OPEN_GROUP_OPTIONS,
            closeGroupOptions: SEARCH_CRITERIA_CLOSE_GROUP_OPTIONS,
            sqlOperatorOptions: SQL_OPERATORS_OPTIONS,
            moveRow,
            handleSelectRow,
            rowsLength: rows.length,
            className: classNames(selectedTableRowId === row.rowId ? classes.selected : ''),
            isSelected: selectedTableRowId === row.rowId,
          }))}
          hidePagination
        />
      </Grid>
    </ExpandablePanel>
  );
};

export default SearchCriteriaTable;
